<template>
  <div class="nav-node">
    <!-- The node's own row: the category link, plus a twisty when it has
         anything to reveal. Brands and subcategories both collapse behind it,
         so a category with many brands costs one row until it is opened —
         which is what stops the drawer growing into a long scroll. -->
    <div class="nav-node-row">
      <router-link
        :to="toPath(node.path)"
        class="nav-node-link"
        @click.native="$emit('navigate')"
      >
        {{ node.name }}
      </router-link>

      <button
        v-if="isExpandable"
        type="button"
        class="nav-node-toggle"
        :aria-expanded="expanded ? 'true' : 'false'"
        :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${node.name}`"
        @click="expanded = !expanded"
      >
        <i class="fas" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </button>
    </div>

    <div v-if="isExpandable && expanded" class="nav-node-body">
      <!-- Brands filed under this category. Unfiled brands never reach the API,
           so nothing here needs to check visibility. -->
      <router-link
        v-for="brand in node.brands"
        :key="`b-${node.id}-${brand.id}`"
        :to="toPath(node.path + brand.slug + '/')"
        class="nav-node-link nav-node-link--brand"
        @click.native="$emit('navigate')"
      >
        {{ brand.name }}
      </router-link>

      <!-- Subcategories, to any depth. -->
      <div v-if="hasChildren" class="nav-node-children">
        <NavCategoryNode
          v-for="child in node.children"
          :key="child.id"
          :node="child"
          @navigate="$emit('navigate')"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  // Named so the template can recurse — the customer sets the depth (T4).
  name: 'NavCategoryNode',
  props: {
    node: { type: Object, required: true }
  },
  data() {
    // Collapsed by default: the drawer opens as a short, scannable list of
    // categories rather than every brand at once.
    return { expanded: false };
  },
  computed: {
    hasChildren() {
      return Array.isArray(this.node.children) && this.node.children.length > 0;
    },
    hasBrands() {
      return Array.isArray(this.node.brands) && this.node.brands.length > 0;
    },
    isExpandable() {
      return this.hasChildren || this.hasBrands;
    }
  },
  methods: {
    // '/women/bags/' -> '/c/women/bags'
    toPath(path) {
      return `/c/${String(path || '').split('/').filter(Boolean).join('/')}`;
    }
  }
};
</script>

<style scoped>
.nav-node {
  display: block;
}

/* The link takes the free space and the twisty sits hard right, so every
   chevron in the drawer lines up in one column regardless of name length. */
.nav-node-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-node-link {
  display: block;
  flex: 1;
  min-width: 0;
  padding: 10px 0;
  color: #1a1a1a;
  text-decoration: none;
  font-size: 0.93rem;
  /* Long brand names wrapped mid-row and broke the alignment; clip instead. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-node-link:hover {
  text-decoration: underline;
}

.nav-node-link--brand {
  color: #555;
  padding-left: 12px;
}

/* Sized to match the category header's chevron in the drawer, and given a real
   touch target rather than being a bare glyph. */
.nav-node-toggle {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
}

.nav-node-toggle i {
  font-size: 0.75rem;
}

.nav-node-children {
  padding-left: 12px;
  border-left: 1px solid #eee;
  margin-left: 2px;
}
</style>
